/**
 * Hyperliquid API Wallet Service
 *
 * Two implementations:
 *
 * 1. HLApiWalletService (plaintext, sync)
 *    - sessionStorage, no encryption
 *    - Fallback when IndexedDB/Web Crypto unavailable
 *
 * 2. SecureHLApiWalletService (encrypted, async)
 *    - IndexedDB + AES-GCM-256 via SecureKeyStorage
 *    - 30-min in-memory TTL cache
 *    - Monotonic nonces via nextNonce()
 *    - Cross-tab sync via BroadcastChannel
 *    - Requires WalletClient for key derivation
 *
 * The hook layer uses isSecureStorageAvailable() to pick the right one.
 */

import { generatePrivateKey, privateKeyToAccount } from "viem/accounts";
import type { PrivateKeyAccount } from "viem/accounts";
import type { WalletClient } from "viem";
import {
  signPhantomAgent,
  sortOrderAction,
  sortCancelAction,
  sortUpdateLeverageAction,
  generateClientOrderId,
  postExchange,
  parseOrderResult,
  HyperliquidInfoClient,
  HL_DEFAULT_SLIPPAGE,
  calculateSlippagePrice,
} from "../client";
import type {
  HLOrderResponse,
  HLBuilderConfig,
  HLOrderWire,
} from "../client";
import { secureKeyStorage, CURRENT_KEY_VERSION } from "./secure-key-storage";
import {
  nextNonce,
  createWalletSyncChannel,
  withMigrationLock,
} from "./cross-tab-sync";
import type { WalletSyncChannel } from "./cross-tab-sync";

// ============================================================================
// Types
// ============================================================================

interface ApiWalletConfig {
  privateKey: `0x${string}`;
  address: `0x${string}`;
  isApproved: boolean;
  mainWalletAddress: string;
}

export interface HLOrderParams {
  /** Base asset name, e.g., "BTC" */
  coin: string;
  side: "buy" | "sell";
  /** Size in base asset units */
  size: string;
  /** Price for limit orders (omit for market) */
  price?: string;
  reduceOnly?: boolean;
  /** Slippage % for market orders (default: 0.1%) */
  maxSlippage?: number;
  /** Include builder fee in order (requires prior builder approval) */
  includeBuilderFee?: boolean;
}

export interface HLOrderResult {
  success: boolean;
  orderId?: number;
  error?: string;
}

export interface HLApiWalletStatus {
  hasWallet: boolean;
  isApproved: boolean;
  walletAddress?: string;
  needsApproval: boolean;
}

// ============================================================================
// Service
// ============================================================================

const STORAGE_KEY_PREFIX = "hl_api_wallet_";

export class HLApiWalletService {
  private config: ApiWalletConfig | null = null;
  private account: PrivateKeyAccount | null = null;
  private infoClient: HyperliquidInfoClient;
  private builder: HLBuilderConfig | null;
  private isMainnet: boolean;

  constructor(
    options: {
      builder?: HLBuilderConfig;
      testnet?: boolean;
      dexNames?: string[];
    } = {},
  ) {
    this.infoClient = new HyperliquidInfoClient({
      testnet: options.testnet,
      dexNames: options.dexNames,
    });
    this.builder = options.builder ?? null;
    this.isMainnet = !options.testnet;
  }

  // ==========================================================================
  // Wallet Lifecycle
  // ==========================================================================

  /**
   * Generate a new ephemeral API wallet. Stores in sessionStorage.
   */
  generateWallet(mainWalletAddress: string): ApiWalletConfig {
    const privateKey = generatePrivateKey();
    const viemAccount = privateKeyToAccount(privateKey);

    const walletConfig: ApiWalletConfig = {
      privateKey,
      address: viemAccount.address,
      isApproved: false,
      mainWalletAddress: mainWalletAddress.toLowerCase(),
    };

    this.storeConfig(mainWalletAddress, walletConfig);
    this.config = walletConfig;
    this.account = null; // Not approved yet

    return walletConfig;
  }

  /**
   * Load existing API wallet from sessionStorage.
   * Returns null if no wallet exists for this main wallet address.
   */
  loadWallet(mainWalletAddress: string): ApiWalletConfig | null {
    const walletConfig = this.loadConfig(mainWalletAddress);
    if (!walletConfig) return null;

    this.config = walletConfig;
    if (walletConfig.isApproved) {
      this.account = privateKeyToAccount(walletConfig.privateKey);
    }

    return walletConfig;
  }

  /**
   * Mark API wallet as approved and initialize the signing account.
   * Call this after successful agent approval submission.
   */
  markApproved(mainWalletAddress: string): void {
    if (!this.config) return;

    this.config.isApproved = true;
    this.storeConfig(mainWalletAddress, this.config);
    this.account = privateKeyToAccount(this.config.privateKey);
  }

  /**
   * Clear the API wallet (logout / session reset).
   */
  clearWallet(mainWalletAddress: string): void {
    this.clearConfig(mainWalletAddress);
    this.config = null;
    this.account = null;
  }

  /**
   * Check the current status of the API wallet.
   */
  getStatus(mainWalletAddress: string): HLApiWalletStatus {
    const walletConfig = this.loadWallet(mainWalletAddress);
    return {
      hasWallet: !!walletConfig,
      isApproved: walletConfig?.isApproved ?? false,
      walletAddress: walletConfig?.address,
      needsApproval: !!walletConfig && !walletConfig.isApproved,
    };
  }

  /** Whether the service is ready to submit orders. */
  isReady(): boolean {
    return !!this.account && !!this.config?.isApproved;
  }

  /** Get the API wallet address (if generated). */
  getWalletAddress(): `0x${string}` | null {
    return this.config?.address ?? null;
  }

  // ==========================================================================
  // Order Placement
  // ==========================================================================

  /**
   * Place a market order (IOC with slippage).
   */
  async placeMarketOrder(params: HLOrderParams): Promise<HLOrderResult> {
    if (!this.account || !this.config?.isApproved) {
      return { success: false, error: "API wallet not ready" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      // Get market price for slippage calculation
      const midStr = await this.infoClient.getMidPrice(params.coin);
      if (!midStr) {
        return { success: false, error: `No price for ${params.coin}` };
      }

      const slippage = params.maxSlippage ?? HL_DEFAULT_SLIPPAGE * 100;
      const priceWithSlippage = calculateSlippagePrice(
        midStr,
        params.side,
        slippage,
      );

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: priceWithSlippage,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: { limit: { tif: "FrontendMarket" } },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Order failed",
      };
    }
  }

  /**
   * Place a limit order (GTC).
   */
  async placeLimitOrder(params: HLOrderParams): Promise<HLOrderResult> {
    if (!this.account || !this.config?.isApproved) {
      return { success: false, error: "API wallet not ready" };
    }

    if (!params.price) {
      return { success: false, error: "Price required for limit order" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: params.price,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: { limit: { tif: "Gtc" } },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Order failed",
      };
    }
  }

  /**
   * Place a trigger order (stop market, stop limit, TP, SL).
   * Uses the HL trigger wire format instead of limit.
   */
  async placeTriggerOrder(params: {
    coin: string;
    side: "buy" | "sell";
    size: string;
    triggerPrice: string;
    isMarket: boolean;
    tpsl: "tp" | "sl";
    reduceOnly?: boolean;
    price?: string;
    includeBuilderFee?: boolean;
  }): Promise<HLOrderResult> {
    if (!this.account || !this.config?.isApproved) {
      return { success: false, error: "API wallet not ready" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      // For stop_limit, use the provided limit price; for stop_market, use trigger price
      const orderPrice = params.isMarket
        ? params.triggerPrice
        : (params.price ?? params.triggerPrice);

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: orderPrice,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: {
          trigger: {
            isMarket: params.isMarket,
            triggerPx: params.triggerPrice,
            tpsl: params.tpsl,
          },
        },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Trigger order failed",
      };
    }
  }

  /**
   * Update leverage for a specific asset.
   *
   * POSTs to /exchange with action:
   * { type: "updateLeverage", asset: <index>, isCross: <bool>, leverage: <int> }
   */
  async updateLeverage(params: {
    asset: string;
    leverage: number;
    isCross: boolean;
  }): Promise<{ success: boolean; error?: string }> {
    if (!this.account || !this.config?.isApproved) {
      return { success: false, error: "API wallet not ready" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.asset);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.asset}` };
      }

      const rawAction: Record<string, unknown> = {
        type: "updateLeverage",
        asset: assetIndex,
        isCross: params.isCross,
        leverage: params.leverage,
      };

      // Sort keys to canonical order for msgpack serialization
      const action = sortUpdateLeverageAction(rawAction);

      const nonce = nextNonce();
      const signature = await signPhantomAgent(
        this.account,
        action,
        nonce,
        this.isMainnet,
      );

      const result = await postExchange(action, nonce, signature, {
        testnet: !this.isMainnet,
      });

      return { success: result.status === "ok" };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Leverage update failed",
      };
    }
  }

  /**
   * Cancel an order by OID.
   */
  async cancelOrder(
    coin: string,
    orderId: number,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.account || !this.config?.isApproved) {
      return { success: false, error: "API wallet not ready" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${coin}` };
      }

      const rawAction: Record<string, unknown> = {
        type: "cancel",
        cancels: [{ a: assetIndex, o: orderId }],
      };

      // Sort keys to canonical order for msgpack serialization
      const action = sortCancelAction(rawAction);

      const nonce = nextNonce();
      const signature = await signPhantomAgent(
        this.account,
        action,
        nonce,
        this.isMainnet,
      );

      const result = await postExchange(action, nonce, signature, {
        testnet: !this.isMainnet,
      });

      return { success: result.status === "ok" };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Cancel failed",
      };
    }
  }

  // ==========================================================================
  // Internal
  // ==========================================================================

  private async submitOrder(
    orders: HLOrderWire[],
    includeBuilderFee?: boolean,
  ): Promise<HLOrderResult> {
    if (!this.account) {
      return { success: false, error: "No signing account" };
    }

    // Build the raw action
    const rawAction: Record<string, unknown> = {
      type: "order",
      orders,
      grouping: "na",
    };

    if (includeBuilderFee && this.builder) {
      rawAction.builder = {
        b: this.builder.address,
        f: this.builder.feeRate,
      };
    }

    // Sort keys to canonical order — msgpack serialization is order-dependent
    const action = sortOrderAction(rawAction);

    const nonce = nextNonce();
    const signature = await signPhantomAgent(
      this.account,
      action,
      nonce,
      this.isMainnet,
    );

    const response = await postExchange<HLOrderResponse>(
      action,
      nonce,
      signature,
      { testnet: !this.isMainnet },
    );

    return parseOrderResult(response);
  }

  // ==========================================================================
  // Storage (sessionStorage — cleared on tab close)
  // ==========================================================================

  private storageKey(mainWalletAddress: string): string {
    return `${STORAGE_KEY_PREFIX}${mainWalletAddress.toLowerCase()}`;
  }

  private storeConfig(
    mainWalletAddress: string,
    walletConfig: ApiWalletConfig,
  ): void {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(
      this.storageKey(mainWalletAddress),
      JSON.stringify(walletConfig),
    );
  }

  private loadConfig(mainWalletAddress: string): ApiWalletConfig | null {
    if (typeof window === "undefined") return null;
    const stored = sessionStorage.getItem(this.storageKey(mainWalletAddress));
    if (!stored) return null;
    return JSON.parse(stored) as ApiWalletConfig;
  }

  private clearConfig(mainWalletAddress: string): void {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem(this.storageKey(mainWalletAddress));
  }
}

// ============================================================================
// Capability Detection
// ============================================================================

/**
 * Check whether the browser supports IndexedDB + Web Crypto (required for
 * SecureHLApiWalletService). Returns false during SSR or in old browsers.
 */
export function isSecureStorageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof indexedDB === "undefined") return false;
  if (typeof crypto === "undefined" || typeof crypto.subtle === "undefined")
    return false;
  return true;
}

// ============================================================================
// Secure API Wallet Service
// ============================================================================

/** Cache TTL: 30 minutes in milliseconds */
const CACHE_TTL_MS = 30 * 60 * 1000;

/**
 * Encrypted variant of HLApiWalletService.
 *
 * Uses SecureKeyStorage (IndexedDB + AES-GCM-256) instead of sessionStorage.
 * All lifecycle methods are async because storage operations require
 * WalletClient.signMessage() for key derivation.
 *
 * In-memory cache (30-min TTL) avoids re-deriving the encryption key on
 * every operation. Cache is invalidated on TTL expiry, cross-tab broadcasts,
 * explicit clear, and BroadcastChannel messages from other tabs.
 */
export class SecureHLApiWalletService {
  // ES private fields — true runtime privacy. Object.keys(), JSON.stringify(),
  // bracket access, and React fiber traversal cannot reach these fields.
  // This makes the "key held in private class field" security claim (A7) truthful.

  // In-memory cache (key material)
  #cachedConfig: ApiWalletConfig | null = null;
  #cachedAccount: PrivateKeyAccount | null = null;
  #cacheTimestamp = 0;

  // Wallet client for key derivation (set during init/load)
  private walletClient: WalletClient | null = null;

  // Dependencies
  private infoClient: HyperliquidInfoClient;
  private builder: HLBuilderConfig | null;
  private isMainnet: boolean;

  // Cross-tab sync
  #syncChannel: WalletSyncChannel;
  private currentUserAddress: string | null = null;

  // Proactive idle eviction — clears in-memory key material after CACHE_TTL_MS
  // of inactivity, making the "auto-eviction on expiry" claim truthful.
  #evictionTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    options: {
      builder?: HLBuilderConfig;
      testnet?: boolean;
      dexNames?: string[];
    } = {},
  ) {
    this.infoClient = new HyperliquidInfoClient({
      testnet: options.testnet,
      dexNames: options.dexNames,
    });
    this.builder = options.builder ?? null;
    this.isMainnet = !options.testnet;

    // Set up cross-tab sync channel
    this.#syncChannel = createWalletSyncChannel();
    this.#syncChannel.subscribe((msg) => {
      // If another tab wrote/cleared the wallet for our user, invalidate cache
      if (
        this.currentUserAddress &&
        msg.userAddress === this.currentUserAddress.toLowerCase()
      ) {
        this.invalidateCache();
      }
    });
  }

  // ==========================================================================
  // Wallet Lifecycle (async)
  // ==========================================================================

  /**
   * Generate a new ephemeral API wallet and encrypt it into IndexedDB.
   * Requires WalletClient for key derivation during encryption.
   */
  async generateWallet(
    mainWalletAddress: string,
    walletClient: WalletClient,
  ): Promise<ApiWalletConfig> {
    this.walletClient = walletClient;
    this.currentUserAddress = mainWalletAddress;

    const privateKey = generatePrivateKey();
    const viemAccount = privateKeyToAccount(privateKey);

    const walletConfig: ApiWalletConfig = {
      privateKey,
      address: viemAccount.address,
      isApproved: false,
      mainWalletAddress: mainWalletAddress.toLowerCase(),
    };

    // Encrypt and store in IndexedDB with migration lock
    await withMigrationLock(mainWalletAddress, async () => {
      await secureKeyStorage.storeKey(
        mainWalletAddress,
        privateKey,
        viemAccount.address,
        walletClient,
        {
          isApproved: false,
          createdAt: new Date().toISOString(),
        },
      );
    });

    // Populate memory cache and start proactive eviction timer
    this.#cachedConfig = walletConfig;
    this.#cachedAccount = null; // Not approved yet
    this.#cacheTimestamp = Date.now();
    this.resetEvictionTimer();

    // Notify other tabs
    this.#syncChannel.broadcast({
      type: "wallet-written",
      userAddress: mainWalletAddress.toLowerCase(),
      timestamp: Date.now(),
    });

    return walletConfig;
  }

  /**
   * Load existing API wallet from IndexedDB (decrypts using WalletClient).
   * Returns null if no wallet exists for this address.
   *
   * Uses in-memory cache with 30-min TTL to avoid re-deriving on every call.
   */
  async loadWallet(
    mainWalletAddress: string,
    walletClient: WalletClient,
  ): Promise<ApiWalletConfig | null> {
    this.walletClient = walletClient;
    this.currentUserAddress = mainWalletAddress;

    // Check cache first
    if (this.isCacheValid() && this.#cachedConfig) {
      return this.#cachedConfig;
    }

    // Cache miss or expired — decrypt from IndexedDB
    const result = await secureKeyStorage.retrieveKey(
      mainWalletAddress,
      walletClient,
    );
    if (!result) return null;

    const walletConfig: ApiWalletConfig = {
      privateKey: result.privateKey as `0x${string}`,
      address: result.metadata.address as `0x${string}`,
      isApproved: result.metadata.isApproved,
      mainWalletAddress: mainWalletAddress.toLowerCase(),
    };

    // Populate cache and start proactive eviction timer
    this.#cachedConfig = walletConfig;
    this.#cacheTimestamp = Date.now();
    this.resetEvictionTimer();

    if (walletConfig.isApproved) {
      this.#cachedAccount = privateKeyToAccount(walletConfig.privateKey);
    } else {
      this.#cachedAccount = null;
    }

    return walletConfig;
  }

  /**
   * Mark API wallet as approved and initialize the signing account.
   * Updates metadata in IndexedDB (no re-encryption needed).
   */
  async markApproved(mainWalletAddress: string): Promise<void> {
    if (!this.#cachedConfig) return;

    // Update metadata in IndexedDB
    await secureKeyStorage.updateMetadata(mainWalletAddress, {
      isApproved: true,
      approvedAt: new Date().toISOString(),
    });

    // Update cache
    this.#cachedConfig.isApproved = true;
    this.#cachedAccount = privateKeyToAccount(this.#cachedConfig.privateKey);
    this.#cacheTimestamp = Date.now();

    // Notify other tabs
    this.#syncChannel.broadcast({
      type: "wallet-written",
      userAddress: mainWalletAddress.toLowerCase(),
      timestamp: Date.now(),
    });
  }

  /**
   * Clear the API wallet from IndexedDB and invalidate all caches.
   */
  async clearWallet(mainWalletAddress: string): Promise<void> {
    await secureKeyStorage.clearKey(mainWalletAddress);
    this.invalidateCache();

    // Notify other tabs
    this.#syncChannel.broadcast({
      type: "wallet-cleared",
      userAddress: mainWalletAddress.toLowerCase(),
      timestamp: Date.now(),
    });
  }

  /**
   * Check the current status of the API wallet.
   * Uses metadata (no decryption required — fast, no WalletClient needed).
   */
  async getStatus(mainWalletAddress: string): Promise<HLApiWalletStatus> {
    // Check cache first
    if (this.isCacheValid() && this.#cachedConfig) {
      return {
        hasWallet: true,
        isApproved: this.#cachedConfig.isApproved,
        walletAddress: this.#cachedConfig.address,
        needsApproval: !this.#cachedConfig.isApproved,
      };
    }

    // Fallback to metadata check (no decrypt)
    const hasKey = await secureKeyStorage.hasKey(mainWalletAddress);
    if (!hasKey) {
      return {
        hasWallet: false,
        isApproved: false,
        needsApproval: false,
      };
    }

    const metadata = await secureKeyStorage.getMetadata(mainWalletAddress);
    return {
      hasWallet: true,
      isApproved: metadata?.isApproved ?? false,
      walletAddress: metadata?.address,
      needsApproval: !metadata?.isApproved,
    };
  }

  /** Whether the service is ready to submit orders. */
  isReady(): boolean {
    return (
      this.isCacheValid() &&
      !!this.#cachedAccount &&
      !!this.#cachedConfig?.isApproved
    );
  }

  /** Get the API wallet address (if generated). */
  getWalletAddress(): `0x${string}` | null {
    return this.#cachedConfig?.address ?? null;
  }

  // ==========================================================================
  // Order Placement
  // ==========================================================================

  /**
   * Place a market order (IOC with slippage).
   * Ensures account is valid (re-derives if cache expired) before signing.
   */
  async placeMarketOrder(params: HLOrderParams): Promise<HLOrderResult> {
    const ensureResult = await this.ensureAccount();
    if (!ensureResult.success) return ensureResult;

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      const midStr = await this.infoClient.getMidPrice(params.coin);
      if (!midStr) {
        return { success: false, error: `No price for ${params.coin}` };
      }

      const slippage = params.maxSlippage ?? HL_DEFAULT_SLIPPAGE * 100;
      const priceWithSlippage = calculateSlippagePrice(
        midStr,
        params.side,
        slippage,
      );

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: priceWithSlippage,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: { limit: { tif: "FrontendMarket" } },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Order failed",
      };
    }
  }

  /**
   * Place a limit order (GTC).
   */
  async placeLimitOrder(params: HLOrderParams): Promise<HLOrderResult> {
    const ensureResult = await this.ensureAccount();
    if (!ensureResult.success) return ensureResult;

    if (!params.price) {
      return { success: false, error: "Price required for limit order" };
    }

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: params.price,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: { limit: { tif: "Gtc" } },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Order failed",
      };
    }
  }

  /**
   * Place a trigger order (stop market, stop limit, TP, SL).
   * Uses the HL trigger wire format instead of limit.
   */
  async placeTriggerOrder(params: {
    coin: string;
    side: "buy" | "sell";
    size: string;
    triggerPrice: string;
    isMarket: boolean;
    tpsl: "tp" | "sl";
    reduceOnly?: boolean;
    price?: string;
    includeBuilderFee?: boolean;
  }): Promise<HLOrderResult> {
    const ensureResult = await this.ensureAccount();
    if (!ensureResult.success) return ensureResult;

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.coin}` };
      }

      // For stop_limit, use the provided limit price; for stop_market, use trigger price
      const orderPrice = params.isMarket
        ? params.triggerPrice
        : (params.price ?? params.triggerPrice);

      const order: HLOrderWire = {
        a: assetIndex,
        b: params.side === "buy",
        p: orderPrice,
        s: params.size,
        r: params.reduceOnly ?? false,
        t: {
          trigger: {
            isMarket: params.isMarket,
            triggerPx: params.triggerPrice,
            tpsl: params.tpsl,
          },
        },
        c: generateClientOrderId(),
      };

      return this.submitOrder([order], params.includeBuilderFee);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Trigger order failed",
      };
    }
  }

  /**
   * Update leverage for a specific asset.
   */
  async updateLeverage(params: {
    asset: string;
    leverage: number;
    isCross: boolean;
  }): Promise<{ success: boolean; error?: string }> {
    const ensureResult = await this.ensureAccount();
    if (!ensureResult.success)
      return { success: false, error: ensureResult.error };

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(params.asset);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${params.asset}` };
      }

      const rawAction: Record<string, unknown> = {
        type: "updateLeverage",
        asset: assetIndex,
        isCross: params.isCross,
        leverage: params.leverage,
      };

      const action = sortUpdateLeverageAction(rawAction);
      const nonce = nextNonce();
      const signature = await signPhantomAgent(
        this.#cachedAccount!,
        action,
        nonce,
        this.isMainnet,
      );

      const result = await postExchange(action, nonce, signature, {
        testnet: !this.isMainnet,
      });

      return { success: result.status === "ok" };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Leverage update failed",
      };
    }
  }

  /**
   * Cancel an order by OID.
   */
  async cancelOrder(
    coin: string,
    orderId: number,
  ): Promise<{ success: boolean; error?: string }> {
    const ensureResult = await this.ensureAccount();
    if (!ensureResult.success)
      return { success: false, error: ensureResult.error };

    try {
      await this.infoClient.ensureAssetCache();
      const assetIndex = this.infoClient.getAssetIndex(coin);
      if (assetIndex === undefined) {
        return { success: false, error: `Unknown asset: ${coin}` };
      }

      const rawAction: Record<string, unknown> = {
        type: "cancel",
        cancels: [{ a: assetIndex, o: orderId }],
      };

      const action = sortCancelAction(rawAction);
      const nonce = nextNonce();
      const signature = await signPhantomAgent(
        this.#cachedAccount!,
        action,
        nonce,
        this.isMainnet,
      );

      const result = await postExchange(action, nonce, signature, {
        testnet: !this.isMainnet,
      });

      return { success: result.status === "ok" };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Cancel failed",
      };
    }
  }

  /**
   * Rotate the stored encryption key to the current (or specified) version.
   *
   * Use when the user suspects compromise or to force migration to stronger
   * derivation parameters. After rotation:
   * - IndexedDB record is re-encrypted with new version's params
   * - In-memory cache is invalidated (forces re-derive on next operation)
   * - Other tabs are notified via BroadcastChannel
   */
  async rotateKey(walletClient: WalletClient): Promise<void> {
    if (!this.currentUserAddress) {
      throw new Error("No user address set — call loadWallet first");
    }

    await withMigrationLock(this.currentUserAddress, async () => {
      await secureKeyStorage.rotateKey(this.currentUserAddress!, walletClient);
    });

    // Invalidate in-memory cache — forces re-derive on next operation
    this.invalidateCache();

    // Notify other tabs
    this.#syncChannel.broadcast({
      type: "wallet-rotated",
      userAddress: this.currentUserAddress.toLowerCase(),
      timestamp: Date.now(),
      newVersion: CURRENT_KEY_VERSION,
    });
  }

  /**
   * Close the cross-tab sync channel and clear timers. Call on cleanup/unmount.
   */
  destroy(): void {
    this.clearEvictionTimer();
    this.#syncChannel.close();
    this.invalidateCache();
  }

  // ==========================================================================
  // Internal
  // ==========================================================================

  /**
   * Ensure the signing account is available and cache is valid.
   * If cache expired, re-derives from IndexedDB using walletClient.
   */
  private async ensureAccount(): Promise<HLOrderResult> {
    if (this.isCacheValid() && this.#cachedAccount) {
      this.resetEvictionTimer();
      return { success: true };
    }

    // Cache expired — need to re-derive
    if (!this.walletClient || !this.currentUserAddress) {
      return {
        success: false,
        error: "Wallet client unavailable for key re-derivation",
      };
    }

    const loaded = await this.loadWallet(
      this.currentUserAddress,
      this.walletClient,
    );
    if (!loaded || !this.#cachedAccount) {
      return { success: false, error: "API wallet not ready" };
    }

    this.resetEvictionTimer();
    return { success: true };
  }

  private async submitOrder(
    orders: HLOrderWire[],
    includeBuilderFee?: boolean,
  ): Promise<HLOrderResult> {
    if (!this.#cachedAccount) {
      return { success: false, error: "No signing account" };
    }

    const rawAction: Record<string, unknown> = {
      type: "order",
      orders,
      grouping: "na",
    };

    if (includeBuilderFee && this.builder) {
      rawAction.builder = {
        b: this.builder.address,
        f: this.builder.feeRate,
      };
    }

    const action = sortOrderAction(rawAction);
    const nonce = nextNonce();
    const signature = await signPhantomAgent(
      this.#cachedAccount,
      action,
      nonce,
      this.isMainnet,
    );

    const response = await postExchange<HLOrderResponse>(
      action,
      nonce,
      signature,
      { testnet: !this.isMainnet },
    );

    return parseOrderResult(response);
  }

  // ==========================================================================
  // Cache Management
  // ==========================================================================

  private isCacheValid(): boolean {
    if (!this.#cacheTimestamp) return false;
    return Date.now() - this.#cacheTimestamp < CACHE_TTL_MS;
  }

  private invalidateCache(): void {
    this.clearEvictionTimer();
    this.#cachedConfig = null;
    this.#cachedAccount = null;
    this.#cacheTimestamp = 0;
  }

  /**
   * Start (or restart) the proactive eviction timer.
   * After CACHE_TTL_MS of inactivity, in-memory key material is wiped
   * automatically — even if no code path calls ensureAccount().
   * This makes the "auto-eviction on expiry" security claim truthful:
   * if the user walks away, the key does not stay in memory indefinitely.
   */
  private resetEvictionTimer(): void {
    this.clearEvictionTimer();
    this.#cacheTimestamp = Date.now();
    this.#evictionTimer = setTimeout(() => {
      this.invalidateCache();
    }, CACHE_TTL_MS);
  }

  private clearEvictionTimer(): void {
    if (this.#evictionTimer !== null) {
      clearTimeout(this.#evictionTimer);
      this.#evictionTimer = null;
    }
  }
}
